import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';

import moment from 'moment';

import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import HeaderMobile from 'components/HeaderMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { AirFareConditions } from 'models/flightValidation';
import { TravelDetails } from 'models/travelDetails';
import { copyContent } from 'utils/clipboard';
import { reservationCompanyImage, reservationImageByDestination } from 'utils/images';

import BookingHistory from '../BookingHistory';
import ExchangeWarning from '../ExchangeWarning';
import Timeline from '../Timeline';
import CancellationPolicies from './components/CancellationPolicies';
import MenuItem from './components/MenuItem';
import PassengerBox from './components/PassengerBox';
import PriceBox from './components/PriceBox';
import SectorDetails from './components/SectorDetails';
import {
  Header,
  HeaderButton,
  Container,
  Destination,
  GoingIn,
  Content,
  Menu,
  AirIcon,
  IconCopyPaste,
  IconExchange,
  IconCancel,
  IconHistory,
  IconHelp,
  Wrapper,
  Section,
} from './styles';

type Props = {
  show: boolean;
  onBack: () => void;
  onClose: () => void;
  onChange?: (airFareConditions?: AirFareConditions[]) => void;
  onCancel?: () => void;
  data: TravelDetails;
  travelId?: string;
};

const FlightDetailsSection = ({ show, onClose, onBack, onChange, onCancel, data, travelId }: Props) => {
  const { t } = useStoreTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const arrivalDate = useMemo(
    () =>
      data.flightSectors && data.flightSectors[0] && data.flightSectors[0].flights
        ? data.flightSectors[0].flights[0].arrivalDate
        : new Date(),
    [data.flightSectors],
  );
  const history = useHistory();
  const { airlineConfirmationCode } = data.requester;
  const arrivalCityName =
    data.flightSectors && data.flightSectors[0] && data.flightSectors[0].flights
      ? data.flightSectors[0].flights.last().arrivalCityName
      : '';
  const arrivalCode =
    data.flightSectors && data.flightSectors[0] && data.flightSectors[0].flights
      ? data.flightSectors[0].flights.last().arrivalAirport
      : undefined;
  const airlineCode =
    data.flightSectors && data.flightSectors[0] && data.flightSectors[0].flights
      ? data.flightSectors[0].flights[0].airlineCode
      : null;
  const airlineLogo = useMemo(() => {
    if (airlineCode) {
      return reservationCompanyImage(airlineCode);
    }
    return null;
  }, [airlineCode]);
  const [showHistory, setShowHistory] = useState(false);
  const handleHelp = () => history.push(`/customerSupport/trip/${travelId}`);
  const capitalizeFirstLetter = (string) => string.charAt(0).toUpperCase() + string.slice(1);
  const expirationTime = useMemo(() => capitalizeFirstLetter(moment(arrivalDate).fromNow()), [arrivalDate]);
  const isActive = useMemo(
    () =>
      data.air &&
      data.air.sectorsExchanged &&
      data.air.flightExchangedPending === true &&
      data.air.flightExchangedConfirmed === false
        ? true
        : false,
    [data.air],
  );
  const note = useMemo(() => {
    if (isActive) {
      return t('flightDetails.section.note');
    }
    return undefined;
  }, [isActive, t]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedTab, setSelectedTab] = useState('1');
  const handleChange = useCallback(() => {
    if (
      data.air &&
      data.air?.sectorsExchanged &&
      (data.air.flightExchangedConfirmed || data.air.flightExchangedPending)
    ) {
      setShowConfirm(true);
    } else if (onChange !== undefined) {
      onChange(data.air?.airFareConditions);
    }
  }, [data.air, onChange]);

  if (showHistory)
    return (
      <BookingHistory
        show={showHistory}
        onClose={() => setShowHistory(false)}
        onBack={() => setShowHistory(false)}
        onDetails={(isExchange: boolean) => {
          setShowHistory(false);
          setTimeout(() => {
            setSelectedTab(isExchange ? '2' : '1');
            if (ref.current && detailRef.current && ref.current.parentElement) {
              const top = detailRef.current.offsetTop + 143;
              ref.current.parentElement.scrollTo({ top, behavior: 'smooth' });
            }
          }, 500);
        }}
        data={data}
      />
    );

  const ExchangeMenuItem = () =>
    note ? (
      <MenuItem
        label={t('flightDetails.section.menu.exchange')}
        isActive={isActive}
        menuIcon={<IconExchange />}
        onClick={handleChange}
        hasNote={true}
        note={note}
      />
    ) : (
      <MenuItem
        label={t('flightDetails.section.menu.exchange')}
        isActive={isActive}
        menuIcon={<IconExchange />}
        onClick={handleChange}
      />
    );

  return (
    <>
      <ExchangeWarning
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={() => {
          setShowConfirm(false);
          if (onChange) onChange(data.air?.airFareConditions);
        }}
        isLoading={false}
      />
      <ApplicationOverlayContent sidebar show={show} onClose={onClose} backgroundColor="#f9f9f9">
        <HeaderMobile
          goBack={onBack}
          onClose={onClose}
          rightButton={
            <HeaderButton>
              <HeaderButton.Button onClick={handleHelp}>
                <HeaderButton.Icon />
              </HeaderButton.Button>
              <HeaderButton.Label>{t('flightDetails.section.help')}</HeaderButton.Label>
            </HeaderButton>
          }
        >
          <Header>
            <Header.Title>
              {t('flightDetails.section.title')} {arrivalCityName}
            </Header.Title>
            <Header.Subtitle>
              {t('flightDetails.section.subtitle')} {data.air?.orderNumber}
            </Header.Subtitle>
          </Header>
        </HeaderMobile>
        <Container ref={ref}>
          <GoingIn>
            <GoingIn.Icon />
            <GoingIn.Text>{expirationTime}</GoingIn.Text>
          </GoingIn>
          {arrivalCode && <Destination $image={reservationImageByDestination(arrivalCode)} />}
          <Wrapper>
            <Content>
              <Content.Header>
                <Content.Title>{t('flightDetails.section.headerTitle')}</Content.Title>
                <Content.Image src="/images/caller.png" width={80} style={{ marginRight: '-10px' }} />
              </Content.Header>
              <Menu>
                <Menu.Header>
                  <Menu.Title>{t('flightDetails.section.airlineCode')}</Menu.Title>
                  <Menu.Text>{t('flightDetails.section.airlineText')}</Menu.Text>
                  <Menu.Content onClick={() => copyContent(airlineConfirmationCode)}>
                    <Menu.Code>
                      <Menu.Icon>{airlineLogo && <AirIcon src={airlineLogo} />}</Menu.Icon>
                      {airlineConfirmationCode}
                    </Menu.Code>
                    <Menu.Arrow>
                      <IconCopyPaste />
                    </Menu.Arrow>
                  </Menu.Content>
                </Menu.Header>
                <Menu.Group>
                  {onChange && 1 > 2 && <ExchangeMenuItem />}
                  {onCancel && 1 > 2 && (
                    <MenuItem
                      label={t('flightDetails.section.menu.cancel')}
                      menuIcon={<IconCancel />}
                      onClick={() => {
                        onCancel();
                      }}
                    />
                  )}

                  <MenuItem
                    label={t('flightDetails.section.menu.history')}
                    isActive={
                      data.air && data.air.sectorsExchanged && data.air.sectorsExchanged.length > 0 ? true : false
                    }
                    menuIcon={<IconHistory />}
                    onClick={() => setShowHistory(true)}
                  />

                  <MenuItem label={t('flightDetails.section.menu.help')} menuIcon={<IconHelp />} onClick={handleHelp} />
                </Menu.Group>
              </Menu>
            </Content>
            <Content>
              <Content.Header>
                <Content.Title>{t('flightDetails.section.vouchers.title')}</Content.Title>
                <Content.Image src="/images/vouchers.png" />
              </Content.Header>
              <Menu>
                <Menu.Header>
                  <Menu.Title>{t('flightDetails.section.vouchers.subtitle')}</Menu.Title>
                  <Menu.Code>{data.requester.email}</Menu.Code>
                  <Menu.Text>{t('flightDetails.section.vouchers.text')}</Menu.Text>
                </Menu.Header>
                {/* <Menu.Group>
                  <MenuItem label="Enviar por email" menuIcon={<IconEmail />} onClick={() => console.log('e-mail')} />
                </Menu.Group> */}
              </Menu>
            </Content>
            <Section ref={detailRef}>
              <Section.Title>{t('flightDetails.section.detail.title')}</Section.Title>
              <SectorDetails
                sectors={data.flightSectors ?? []}
                status={data.requester.status}
                onCancel={onCancel}
                exchangedSectors={data.air?.sectorsExchanged ?? []}
                exchangeConfirmed={data.air?.flightExchangedConfirmed}
                exchangePending={data.air?.flightExchangedPending}
                selectedTab={selectedTab}
              />
            </Section>
            {data.air && data.air.airFareConditions && (
              <Section>
                <Section.Title>{t('flightDetails.section.cancellation.title')}</Section.Title>
                <Section.Text>{t('flightDetails.section.cancellation.text')}</Section.Text>
                <CancellationPolicies policies={data.air.airFareConditions} />
              </Section>
            )}
            <Section>
              <Section.Title>{t('flightDetails.section.passengers.title')}</Section.Title>
              <Section.Text>{t('flightDetails.section.passengers.text')}</Section.Text>
              <PassengerBox requester={data.requester} />
            </Section>
            <Section>
              <Section.Title>{t('flightDetails.section.requestHistory.title')}</Section.Title>
              {data.travelLogs.length > 0 && <Timeline data={data} />}
            </Section>
            {data.prices && (
              <Section>
                <Section.Title>{t('flightDetails.section.price.title')}</Section.Title>
                <Section.Text>{t('flightDetails.section.price.text')}</Section.Text>
                <PriceBox price={data.prices} />
              </Section>
            )}
          </Wrapper>
        </Container>
      </ApplicationOverlayContent>
    </>
  );
};
export default FlightDetailsSection;
